import { ErrorLink } from '@apollo/client/link/error';
import { catchError, from, map, of, switchMap, throwError } from 'rxjs';

import { isExpectedSessionEndError } from '@/adapters/auth/tokenRefreshManager';
import type { TokenRefreshManager } from '@/adapters/auth/tokenRefreshManager';
import {
  hasUnauthenticatedError,
  hasUnauthenticatedResult,
} from '@/adapters/graphql/unauthenticatedError';

const createTokenRefreshLink = (manager: TokenRefreshManager): ErrorLink =>
  new ErrorLink(({ error, result, operation, forward }) => {
    if (!hasUnauthenticatedError(error)) {
      return;
    }

    const retryOperation = () =>
      forward(operation).pipe(
        switchMap((retriedResult) => {
          if (!hasUnauthenticatedResult(retriedResult)) {
            return of(retriedResult);
          }

          return from(manager.handleRetryFailure()).pipe(map(() => retriedResult));
        }),
      );

    const handleSessionEnd = (refreshError: unknown) => {
      if (!isExpectedSessionEndError(refreshError) || !result) {
        return throwError(() => refreshError);
      }

      return of(result);
    };

    return from(manager.attemptRefresh()).pipe(
      switchMap(retryOperation),
      catchError(handleSessionEnd),
    );
  });

export { createTokenRefreshLink };
